import { ChangeDetectionStrategy, Component, inject, input, OnInit, output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { EquationType, ProjectStatusId } from '@app/shared/data/project-enums';
import { ButtonLoadingDirective } from '@app/shared/directives/button-loading/button-loading.directive';
import { IProjectTab } from '@app/shared/interfaces/iproject-tab';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { Equation } from '@app/shared/models';
import { ProjectPadConstructionParameter } from '@app/shared/models/project-pad-construction-parameter';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import { SVGIconComponent } from '@progress/kendo-angular-icons';
import { NumericTextBoxComponent, TextBoxComponent } from '@progress/kendo-angular-inputs';
import { zip as observableZip } from 'rxjs';
import { AuxPadBaseTypeId, CulvertsTypeId, DrillPadBaseTypeId, LineRerouteId, PlannedReservePitBackfillTypeId, SoundMaterialTypeId, TankBatteryBaseTypeId } from '../../enums/pad-construction-parameters';
import { LookupPlusService } from '../../services/lookups/lookup-plus.service';
import { ProjectCustomEndpointService } from '../../services/project-custom-endpoint.service';
import { ProjectEstimateService } from '../../services/project-estimate.service';
import { ProjectObserverService } from '../../services/project-observer.service';

@Component({
  selector: 'app-project-pad-construction-details',
  templateUrl: './project-pad-construction-details.component.html',
  styleUrls: ['./project-pad-construction-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    ButtonLoadingDirective,
    DialogModule,
    DropDownListComponent,
    FormsModule,
    NumericTextBoxComponent,
    ReactiveFormsModule,
    SVGIconComponent,
    TextBoxComponent,
  ],
})
export class ProjectPadConstructionDetailsComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private lookupPlusService = inject(LookupPlusService);
  private projectCustomEndpointService = inject(ProjectCustomEndpointService);
  private estimationService = inject(ProjectEstimateService);
  private poSvc = inject(ProjectObserverService);

  private tRoadType: string = 'RoadTypes';
  private tConstructionSequence: string = 'ConstructionSequences';
  private tDrillPadBaseType: string = 'DrillPadBaseTypes';
  private tTankBatteryBaseType: string = 'TankBatteryBaseTypes';
  private tConstructionFenceType: string = 'ConstructionFenceTypes';
  private tLineReroute: string = 'LineReroutes';
  private tAuxPadBaseType: string = 'AuxPadBaseTypes';
  private tSoundMaterialType: string = 'SoundMaterialTypes';
  private tCulvertsType: string = 'CulvertsTypes';
  private tPlannedReservePitBackfillType: string = 'PlannedReservePitBackfillTypes';

  public icons = kendoIcons;
  public padConstructionForm: UntypedFormGroup;
  public padConstructionParameters: ProjectPadConstructionParameter;
  public decimalPrecision: number = 0;
  public decimalFormat: string = 'n0';
  public dRoadType: any[] = [];
  public dConstructionSequence: any[] = [];
  public dDrillPadBaseType: any[] = [];
  public dTankBatteryBaseType: any[] = [];
  public dConstructionFenceType: any[] = [];
  public dLineReroute: any[] = [];
  public dAuxPadBaseType: any[] = [];
  public dSoundMaterialType: any[] = [];
  public dCulvertsType: any[] = [];
  public dPlannedReservePitBackfillType: any[] = [];
  public loading: boolean = false;
  public showDGWarning: boolean = false;
  public showTBFields: boolean = false;
  public showEquationHelp: boolean = false;
  public equationValue: string = '';
  public padConstructionEquations: Equation[];
  public constructionSubType: string = ''

  readonly tabTdv = output<boolean>();
  readonly iProjectId = input<number>(undefined);
  readonly iProjectTypeId = input<number>(undefined);
  readonly iProjectStatusId = input<number>(undefined);
  readonly iProjectTab = input<IProjectTab>(undefined);

  async ngOnInit() {
    this.generateForm();

    await Promise.all([
      this.getRoadType(this.tRoadType),
      this.getConstructionSequence(this.tConstructionSequence),
      this.getDrillPadBaseType(this.tDrillPadBaseType),
      this.getTankBatteryBaseType(this.tTankBatteryBaseType),
      this.getConstructionFenceType(this.tConstructionFenceType),
      this.getLineReroute(this.tLineReroute),
      this.getAuxPadBaseType(this.tAuxPadBaseType),
      this.getSoundMaterialType(this.tSoundMaterialType),
      this.getCulvertsType(this.tCulvertsType),
      this.getPlannedReservePitBackfillType(this.tPlannedReservePitBackfillType)
    ]);

    if (this.iProjectStatusId() === ProjectStatusId.Closed || this.iProjectStatusId() === ProjectStatusId.Cancelled) {
      this.padConstructionForm.disable();
    }

    this.getPadConstructionEquations(EquationType.PadConstructionParametersCalculations);
    this.getPadConstructionParameters(false);

    this.iProjectTab().ProjectChanged.subscribe((resp) => {
      let constSequence = this.dConstructionSequence.find(x => x.ConstructionSequenceId == resp.ConstructionTypeId);
      this.constructionSubType = constSequence ? constSequence.Description : '';
    });
    this.iProjectTab().ProjectAssetMappingService.hasMultipleGroups.subscribe((resp) => {
      this.showDGWarning = resp;
    });
  }

  private generateForm(): void {
    this.padConstructionForm = this.fb.group({
      WellCount: null,
      TypeCurveArea: null,
      ProposedPadAndAuxPadSurfaceArea: null,
      TankBatterySurfaceArea: null,
      TotalPadSurfaceAreaAndTB: null,
      FuturePadSurfaceArea: null,
      AsBuiltPadSurfaceArea: null,
      TotalRigFoundationVolume: null,
      PadBaseThickness: 4,
      FWPitCutVolume: 2156,
      FWPitLinerArea: 51839,
      ReservePitCutVolume: null,
      FWPitFillVolume: 1105,
      FWPitPerimeter: 600,
      ReservePitLinerArea: null,
      ReservePitFillVolume: null,
      ReservePitPerimeter: null,
      DistanceToRockQuarry: null,
      SoundWallHeightOrMaterial: null,
      CellarBoxCount: null,
      DistanceToDisposal: null,
      PitLinerMatCount: 1,
      SoundWallDuration: null,
      PowerUtilityPolesRerouted: null,
      IsWaterHauledInForConstruction: false,
      IsRockContingency: false,
      IsEngineering: false,
      IsEnvironmental: false,
      RoadTypeId1: null,
      RoadLength1: null,
      RoadTypeId2: null,
      RoadLength2: null,
      RoadTypeId3: null,
      RoadLength3: null,
      ConstructionSequenceId: null,
      PlannedReservePitBackfillTypeId: PlannedReservePitBackfillTypeId.DeepBurial,
      DrillPadBaseTypeId: DrillPadBaseTypeId.Limestone,
      TankBatteryBaseTypeId: TankBatteryBaseTypeId.Limestone,
      ConstructionFenceTypeId: null,
      LineRerouteId: LineRerouteId.NoBurialOrReroute,
      AuxPadBaseTypeId: AuxPadBaseTypeId.Limestone,
      SoundMaterialTypeId: SoundMaterialTypeId.MassLoadedVinyl,
      SoundWallLength: null,
      SoundWallHeight: 24,
      CulvertsTypeId: CulvertsTypeId.Corrugated_Metal_Pipe,
      CulvertsLength: 30,
      CulvertsSize: 18,
      PadWidth: null,
      PadLength: null,
      TruckInFillVolume: null,
      PadWidthOverride: null,
      PadLengthOverride: null,
      WaterTruckingOrSpreading: null,
      IsSimulfrac: false,
      OnPadBattery: false,
      PadCutFillVolume: null,
      PadCutFillVolumeOverride: null,
      DrillGroupCount: null,
      TankBatteryCount: null,
      TBPadWidth: null,
      TBPadLength: null,
      TBPadWidthOverride: null,
      TBPadLengthOverride: null,
      UltimatePadDesign: null,
      ReservePitId: null,
      FreshWaterPitId: null,
      AuxPadSurfaceArea: null,
      FWPitArea: null,
      ReservePitArea: null,
      AuxPadSurfaceAreaOverride: null
    });

    this.padConstructionForm.valueChanges.subscribe(data => {
      this.onValueChanged(data);
      this.showTBFields = data && data.OnPadBattery;
    });
  }

  private onValueChanged(data?: any) {
    if (!this.generateForm) {
      return;
    }
    this.poSvc.projectObserve('parameters', this.padConstructionForm.valid);
    if ((this.padConstructionForm.valid && this.padConstructionForm.dirty)) {
      this.setTDV();
    } else {
      this.clearTDV();
    }
  }

  private getRoadType(table: string) {
    return this.lookupPlusService.getLookupNoCount(table).then(payload => {
      this.dRoadType = payload.value;
    });
  }

  private getConstructionSequence(table: string) {
    this.lookupPlusService.getLookupNoCount(table).then(payload => {
      this.dConstructionSequence = payload.value;
    });
  }

  private getPlannedReservePitBackfillType(table: string) {
    this.lookupPlusService.getLookupNoCount(table).then(payload => {
      this.dPlannedReservePitBackfillType = payload.value;
    });
  }

  private getDrillPadBaseType(table: string) {
    this.lookupPlusService.getLookupNoCount(table).then(payload => {
      this.dDrillPadBaseType = payload.value;
    });
  }

  private getTankBatteryBaseType(table: string) {
    this.lookupPlusService.getLookupNoCount(table).then(payload => {
      this.dTankBatteryBaseType = payload.value;
    });
  }

  private getConstructionFenceType(table: string) {
    this.lookupPlusService.getLookupNoCount(table).then(payload => {
      this.dConstructionFenceType = payload.value;
    });
  }

  private getLineReroute(table: string) {
    this.lookupPlusService.getLookupNoCount(table).then(payload => {
      this.dLineReroute = payload.value;
    });
  }

  private getAuxPadBaseType(table: string) {
    this.lookupPlusService.getLookupNoCount(table).then(payload => {
      this.dAuxPadBaseType = payload.value;
    });
  }

  private getSoundMaterialType(table: string) {
    this.lookupPlusService.getLookupNoCount(table).then(payload => {
      this.dSoundMaterialType = payload.value;
    });
  }

  private getCulvertsType(table: string) {
    this.lookupPlusService.getLookupNoCount(table).then(payload => {
      this.dCulvertsType = payload.value;
    });
  }

  public showEquation(variableName: string) {
    this.equationValue = this.padConstructionEquations.find(x => x.VariableName == variableName).EquationValue;
    this.showEquationHelp = true;
  }

  public updatePadConstructionParameters(e): void {
    const requests = [];
    this.padConstructionParameters.WellCount = this.padConstructionForm.controls.WellCount.value;
    this.padConstructionParameters.TypeCurveArea = this.padConstructionForm.controls.TypeCurveArea.value;
    this.padConstructionParameters.ProposedPadAndAuxPadSurfaceArea = this.padConstructionForm.controls.ProposedPadAndAuxPadSurfaceArea.value;
    this.padConstructionParameters.TankBatterySurfaceArea = this.padConstructionForm.controls.TankBatterySurfaceArea.value;
    this.padConstructionParameters.TotalPadSurfaceAreaAndTB = this.padConstructionForm.controls.TotalPadSurfaceAreaAndTB.value;
    this.padConstructionParameters.FuturePadSurfaceArea = this.padConstructionForm.controls.FuturePadSurfaceArea.value;
    this.padConstructionParameters.AsBuiltPadSurfaceArea = this.padConstructionForm.controls.AsBuiltPadSurfaceArea.value;
    this.padConstructionParameters.TotalRigFoundationVolume = this.padConstructionForm.controls.TotalRigFoundationVolume.value;
    this.padConstructionParameters.PadBaseThickness = this.padConstructionForm.controls.PadBaseThickness.value;
    this.padConstructionParameters.FWPitCutVolume = this.padConstructionForm.controls.FWPitCutVolume.value;
    this.padConstructionParameters.FWPitLinerArea = this.padConstructionForm.controls.FWPitLinerArea.value;
    this.padConstructionParameters.ReservePitCutVolume = this.padConstructionForm.controls.ReservePitCutVolume.value;
    this.padConstructionParameters.FWPitFillVolume = this.padConstructionForm.controls.FWPitFillVolume.value;
    this.padConstructionParameters.FWPitPerimeter = this.padConstructionForm.controls.FWPitPerimeter.value;
    this.padConstructionParameters.ReservePitLinerArea = this.padConstructionForm.controls.ReservePitLinerArea.value;
    this.padConstructionParameters.ReservePitFillVolume = this.padConstructionForm.controls.ReservePitFillVolume.value;
    this.padConstructionParameters.ReservePitPerimeter = this.padConstructionForm.controls.ReservePitPerimeter.value;
    this.padConstructionParameters.DistanceToRockQuarry = this.padConstructionForm.controls.DistanceToRockQuarry.value;
    this.padConstructionParameters.SoundWallHeightOrMaterial = this.padConstructionForm.controls.SoundWallHeightOrMaterial.value;
    this.padConstructionParameters.CellarBoxCount = this.padConstructionForm.controls.CellarBoxCount.value;
    this.padConstructionParameters.DistanceToDisposal = this.padConstructionForm.controls.DistanceToDisposal.value;
    this.padConstructionParameters.PitLinerMatCount = this.padConstructionForm.controls.PitLinerMatCount.value;
    this.padConstructionParameters.SoundWallDuration = this.padConstructionForm.controls.SoundWallDuration.value;
    this.padConstructionParameters.PowerUtilityPolesRerouted = this.padConstructionForm.controls.PowerUtilityPolesRerouted.value;
    this.padConstructionParameters.IsWaterHauledInForConstruction = this.padConstructionForm.controls.IsWaterHauledInForConstruction.value;
    this.padConstructionParameters.IsRockContingency = this.padConstructionForm.controls.IsRockContingency.value;
    this.padConstructionParameters.IsEngineering = this.padConstructionForm.controls.IsEngineering.value;
    this.padConstructionParameters.IsEnvironmental = this.padConstructionForm.controls.IsEnvironmental.value;
    this.padConstructionParameters.RoadLength1 = this.padConstructionForm.controls.RoadLength1.value;
    this.padConstructionParameters.RoadTypeId1 = this.padConstructionForm.controls.RoadTypeId1.value;
    this.padConstructionParameters.RoadLength2 = this.padConstructionForm.controls.RoadLength2.value;
    this.padConstructionParameters.RoadTypeId2 = this.padConstructionForm.controls.RoadTypeId2.value;
    this.padConstructionParameters.RoadLength3 = this.padConstructionForm.controls.RoadLength3.value;
    this.padConstructionParameters.RoadTypeId3 = this.padConstructionForm.controls.RoadTypeId3.value;
    this.padConstructionParameters.ConstructionSequenceId = this.padConstructionForm.controls.ConstructionSequenceId.value;
    this.padConstructionParameters.PlannedReservePitBackfillTypeId = this.padConstructionForm.controls.PlannedReservePitBackfillTypeId.value;
    this.padConstructionParameters.DrillPadBaseTypeId = this.padConstructionForm.controls.DrillPadBaseTypeId.value;
    this.padConstructionParameters.TankBatteryBaseTypeId = this.padConstructionForm.controls.TankBatteryBaseTypeId.value;
    this.padConstructionParameters.ConstructionFenceTypeId = this.padConstructionForm.controls.ConstructionFenceTypeId.value;
    this.padConstructionParameters.LineRerouteId = this.padConstructionForm.controls.LineRerouteId.value;
    this.padConstructionParameters.AuxPadBaseTypeId = this.padConstructionForm.controls.AuxPadBaseTypeId.value;
    this.padConstructionParameters.SoundMaterialTypeId = this.padConstructionForm.controls.SoundMaterialTypeId.value;
    this.padConstructionParameters.SoundWallLength = this.padConstructionForm.controls.SoundWallLength.value;
    this.padConstructionParameters.SoundWallHeight = this.padConstructionForm.controls.SoundWallHeight.value;
    this.padConstructionParameters.CulvertsTypeId = this.padConstructionForm.controls.CulvertsTypeId.value;
    this.padConstructionParameters.CulvertsLength = this.padConstructionForm.controls.CulvertsLength.value;
    this.padConstructionParameters.CulvertsSize = this.padConstructionForm.controls.CulvertsSize.value;
    this.padConstructionParameters.PadWidth = this.padConstructionForm.controls.PadWidth.value;
    this.padConstructionParameters.PadLength = this.padConstructionForm.controls.PadLength.value;
    this.padConstructionParameters.TruckInFillVolume = this.padConstructionForm.controls.TruckInFillVolume.value;
    this.padConstructionParameters.PadWidthOverride = this.padConstructionForm.controls.PadWidthOverride.value;
    this.padConstructionParameters.PadLengthOverride = this.padConstructionForm.controls.PadLengthOverride.value;
    this.padConstructionParameters.WaterTruckingOrSpreading = this.padConstructionForm.controls.WaterTruckingOrSpreading.value;
    this.padConstructionParameters.IsSimulfrac = this.padConstructionForm.controls.IsSimulfrac.value;
    this.padConstructionParameters.OnPadBattery = this.padConstructionForm.controls.OnPadBattery.value;
    this.padConstructionParameters.PadCutFillVolume = this.padConstructionForm.controls.PadCutFillVolume.value;
    this.padConstructionParameters.PadCutFillVolumeOverride = this.padConstructionForm.controls.PadCutFillVolumeOverride.value;
    this.padConstructionParameters.DrillGroupCount = this.padConstructionForm.controls.DrillGroupCount.value;
    this.padConstructionParameters.TankBatteryCount = this.padConstructionForm.controls.TankBatteryCount.value;
    this.padConstructionParameters.TBPadWidth = this.padConstructionForm.controls.TBPadWidth.value;
    this.padConstructionParameters.TBPadLength = this.padConstructionForm.controls.TBPadLength.value;
    this.padConstructionParameters.TBPadWidthOverride = this.padConstructionForm.controls.TBPadWidthOverride.value;
    this.padConstructionParameters.TBPadLengthOverride = this.padConstructionForm.controls.TBPadLengthOverride.value;
    this.padConstructionParameters.UltimatePadDesign = this.padConstructionForm.controls.UltimatePadDesign.value;
    this.padConstructionParameters.ReservePitId = this.padConstructionForm.controls.ReservePitId.value;
    this.padConstructionParameters.FreshWaterPitId = this.padConstructionForm.controls.FreshWaterPitId.value;
    this.padConstructionParameters.AuxPadSurfaceArea = this.padConstructionForm.controls.AuxPadSurfaceArea.value;
    this.padConstructionParameters.FWPitArea = this.padConstructionForm.controls.FWPitArea.value;
    this.padConstructionParameters.ReservePitArea = this.padConstructionForm.controls.ReservePitArea.value;
    this.padConstructionParameters.AuxPadSurfaceAreaOverride = this.padConstructionForm.controls.AuxPadSurfaceAreaOverride.value;

    requests.push(this.projectCustomEndpointService.patch(`ProjectPadConstructionParameters(${this.padConstructionParameters.ProjectPadConstructionParameterId})`, this.padConstructionParameters));
    observableZip(...requests).subscribe(response => {
      this.padConstructionForm.reset();
      this.getPadConstructionParameters(false);
    });
  }

  public cancelChanges(e): void {
    this.padConstructionForm.reset();
    this.getPadConstructionParameters(false);
  }

  public getPadConstructionEquations(equationType: EquationType) {
    this.lookupPlusService.getLookup('Equations', `&$filter=EquationTypeId eq ${equationType}`)
      .then(payload => {
        this.padConstructionEquations = payload.value;
      });
  }

  public getPadConstructionParameters(refreshParam: boolean) {
    this.loading = true;
    this.estimationService.calculatePadConstructionParameters(this.iProjectId(), refreshParam)
      .then(() => {
        this.lookupPlusService.getLookup('ProjectPadConstructionParameters', `&$filter=ProjectId eq ${this.iProjectId()}`)
          .then(payload => {
            this.padConstructionParameters = payload.value[0];
            let constSequence = this.dConstructionSequence.find(x => x.ConstructionSequenceId == this.padConstructionParameters.ConstructionSequenceId);
            this.constructionSubType = constSequence ? constSequence.Description : '';
            this.loading = false;
            if (this.padConstructionParameters == null)
              return;
            this.padConstructionForm.patchValue(this.padConstructionParameters);
          });
      });
  }

  public setTDV(): void {
    let tdvState: boolean = true;
    this.tabTdv.emit(tdvState);
  }

  public clearTDV(): void {
    let tdvState: boolean = false;
    this.tabTdv.emit(tdvState);
  }
}